import {
  StorageUploadController,
  StorageUploadError,
  StorageUploadMetadata,
  StorageUploadResult,
  StorageUploadService,
} from "@/application/upload/ports/StorageUploadService";
import { FileType } from "@/domain/upload/value-objects/FileType";
import { firebaseAuth, firebaseStorage } from "@/lib/firebase";
import { ref, uploadBytesResumable, UploadTask } from "firebase/storage";

interface FirebaseUploadController extends StorageUploadController {
  readonly uploadTask: UploadTask;
}

export class FirebaseStorageUploadService implements StorageUploadService {
  startUpload(params: {
    taskId: string;
    file: File;
    fileName: string;
    fileType: FileType;
    metadata: StorageUploadMetadata;
    onProgress: (progress: number) => void;
    onError: (error: StorageUploadError) => void;
    onSuccess: (result: StorageUploadResult) => void;
  }): StorageUploadController {
    const {
      taskId,
      file,
      fileName,
      fileType,
      metadata,
      onProgress,
      onError,
      onSuccess,
    } = params;

    if (!firebaseAuth) {
      throw new Error("Firebase Auth is not initialized");
    }

    if (!firebaseStorage) {
      throw new Error("Firebase Storage is not initialized");
    }

    const user = firebaseAuth.currentUser;
    if (!user?.uid) {
      const error = new Error("User must be authenticated to upload files");
      // Log additional context for debugging
      console.error("[FirebaseStorageUploadService] User authentication error:", {
        hasAuth: !!firebaseAuth,
        hasUser: !!user,
        hasUid: !!user?.uid,
        userId: user?.uid,
      });
      throw error;
    }

    const storagePath = this.buildStoragePath({
      userId: user.uid,
      taskId,
      fileName,
      fileType,
    });

    const storageRef = ref(firebaseStorage, storagePath);

    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType: metadata.contentType ?? file.type,
      customMetadata: metadata.customMetadata,
    });

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress(progress);
      },
      (error) => {
        onError(error as StorageUploadError);
      },
      () => {
        const fullPath = uploadTask.snapshot.ref.fullPath;
        onSuccess({ fullPath });
      }
    );

    return this.createController(uploadTask);
  }

  private buildStoragePath({
    userId,
    taskId,
    fileName,
    fileType,
  }: {
    userId: string;
    taskId: string;
    fileName: string;
    fileType: FileType;
  }): string {
    const directory = this.resolveDirectory(fileType);
    return `${directory}/${userId}/${taskId}/${fileName}`;
  }

  private resolveDirectory(fileType: FileType): string {
    switch (fileType) {
      case "video":
        return "video";
      case "text":
        return "text";
      default:
        return "audio";
    }
  }

  private createController(uploadTask: UploadTask): FirebaseUploadController {
    return {
      uploadTask,
      cancel: () => {
        uploadTask.cancel();
      },
    };
  }
}
